import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import "./ResetPassword.css";
import base_url from "../api";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [tokenValid, setTokenValid] = useState(null); // null = checking, true = valid, false = invalid

  useEffect(() => {
    // Check if token exists
    if (!token || token.trim() === "") {
      setTokenValid(false);
      setMessage({
        type: "error",
        text: "Invalid or missing reset token. Please request a new password reset link.",
      });
    } else {
      setTokenValid(true);
    }
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear message when user types
    if (message.text) {
      setMessage({ type: "", text: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    // Validation
    if (!formData.newPassword || !formData.confirmPassword) {
      setMessage({
        type: "error",
        text: "Please fill in all fields",
      });
      return;
    }

    if (formData.newPassword.length < 6) {
      setMessage({
        type: "error",
        text: "Password must be at least 6 characters long",
      });
      return;
    }

    if (formData.newPassword.length > 128) {
      setMessage({
        type: "error",
        text: "Password must be less than 128 characters",
      });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({
        type: "error",
        text: "Passwords do not match",
      });
      return;
    }

    if (!token) {
      setMessage({
        type: "error",
        text: "Invalid reset token. Please request a new password reset link.",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${base_url}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({
          type: "success",
          text: data.message || "Password has been reset successfully!",
        });
        // Clear form
        setFormData({
          newPassword: "",
          confirmPassword: "",
        });
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setMessage({
          type: "error",
          text: data.message || "Failed to reset password. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setMessage({
        type: "error",
        text: "An error occurred. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Show invalid state if token is missing
  if (tokenValid === false) {
    return (
      <div className="reset-password-page">
        <div className="reset-password-container">
          <div className="reset-password-card">
            <div className="reset-password-header">
              <h2>Invalid Reset Link</h2>
              <p>The password reset link is invalid or has expired.</p>
            </div>
            {message.text && (
              <div
                className={`message ${message.type === "success" ? "message-success" : "message-error"}`}
              >
                {message.text}
              </div>
            )}
            <div className="reset-password-footer">
              <Link to="/forgot-password" className="link-btn">
                Request New Reset Link
              </Link>
              <p>
                <Link to="/login">Back to Login</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show form (tokenValid is true or null - will be validated on submit)
  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="reset-password-header">
            <h2>Reset Password</h2>
            <p>Enter your new password below.</p>
          </div>

          {message.text && (
            <div
              className={`message ${message.type === "success" ? "message-success" : "message-error"}`}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="reset-password-form">
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="Enter new password"
                required
                minLength={6}
                disabled={loading}
              />
              <span className="form-hint">Minimum 6 characters</span>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm new password"
                required
                minLength={6}
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={loading || !token}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>

          <div className="reset-password-footer">
            <p>
              Remember your password? <Link to="/login">Login here</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;

